import { createRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import { Shuffle } from 'lucide-react'
import { Route as appRoute } from '../../_app'
import { QuestionBankBrowser } from '../../../components/question-bank/QuestionBankBrowser'
import { QuestionBankItemForm } from '../../../components/question-bank/QuestionBankItemForm'
import { CategoryManager } from '../../../components/question-bank/CategoryManager'
import { RandomQuizPickerDialog } from '../../../components/game/RandomQuizPickerDialog'
import type { BankItem } from '../../../components/question-bank/types'

/**
 * Question Bank management page (E2/E3).
 *
 * Reuses the dashboard chrome (gradient backdrop, brand rule, max-w-6xl
 * column) and hosts the shared {@link QuestionBankBrowser} in manage mode.
 * Item create/edit happens in a modal ({@link QuestionBankItemForm}) and the
 * private-category CRUD in another ({@link CategoryManager}); both invalidate
 * the shared `['questionBank', ...]` query keys so the grid stays fresh.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/question-bank/',
  component: QuestionBankIndexPage,
})

function QuestionBankIndexPage() {
  const { t } = useTranslation('quiz')

  // `formItem` distinguishes the three form states: closed (`undefined`),
  // create (`null`), edit (a `BankItem`).
  const [formItem, setFormItem] = useState<BankItem | null | undefined>(undefined)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [randomPickerOpen, setRandomPickerOpen] = useState(false)

  const formOpen = formItem !== undefined

  return (
    <main className="min-h-screen bg-gradient-to-bl from-brand-50/40 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-8">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-900 tracking-tight">
            {t('questionBankTitle')}
          </h1>
          <p className="mt-3 text-brand-700 text-base sm:text-lg max-w-xl leading-relaxed">
            {t('questionBankSubtitle')}
          </p>
          <div className="mt-5">
            <Button
              variant="outline"
              size="lg"
              className="gap-2"
              onClick={() => setRandomPickerOpen(true)}
            >
              <Shuffle className="h-5 w-5" aria-hidden="true" />
              <span>{t('startRandomQuiz')}</span>
            </Button>
          </div>
        </header>

        <QuestionBankBrowser
          mode="manage"
          onCreateItem={() => setFormItem(null)}
          onEditItem={(item) => setFormItem(item)}
          onManageCategories={() => setCategoriesOpen(true)}
        />
      </div>

      <QuestionBankItemForm
        open={formOpen}
        item={formItem ?? null}
        onClose={() => setFormItem(undefined)}
      />

      <CategoryManager
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
      />

      <RandomQuizPickerDialog
        open={randomPickerOpen}
        onClose={() => setRandomPickerOpen(false)}
      />
    </main>
  )
}
